/**
 * Cancelled Items List Component
 *
 * Displays items from cancelled orders that need physical action.
 * BLANK items (not yet printed): Return to shelf
 * PRINTED items (already printed): Mark as defect
 *
 * Scanner workflow:
 * 1. Scan garment QR code
 * 2. Find item in list, scroll to it and highlight
 * 3. Auto-open action confirmation dialog
 * 4. Confirm to process item
 *
 * Extracted from IssuesPage for better code organization.
 */
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, XCircle, Package, Recycle, X, Loader2, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { QRScannerInput } from '@/features/catcher-station/components/QRScannerInput';
import { decodeGarmentQR } from '@/features/production-orders/utils/qr-encoder';
import { playErrorBeep } from '@/lib/sounds';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { usePreprintPermission } from '@/hooks/usePreprintPermission';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';
import {
  useCancelledItems,
  useProcessCancelledItem,
  useProcessCancelledItemsBulk,
} from '../hooks/useCancelledItems';
import { CancelledItemCard } from './CancelledItemCard';
import { cancelledItemConfig } from '../types';
import type { CancelledItem } from '../types';

/** Action type matching backend CancelledItemActionSchema — mirrors CancelledItemCard's. */
type CancelledItemAction = 'return_to_shelf' | 'add_to_preprint' | 'mark_defect';

interface BulkActionSpec {
  /** Which subset of the selection this action targets — BLANK or PRINTED garments. */
  source: 'blank' | 'printed';
  /** Toolbar button copy (interpolates the targeted `count`). */
  toolbarKey: string;
  /** Confirmation-dialog body copy (interpolates the targeted `count`). */
  confirmBodyKey: string;
  /** Confirmation-dialog confirm-button copy. */
  confirmLabelKey: string;
  /** Toolbar button colour — the toolbar renders every action as a filled button. */
  toolbarClassName: string;
  /** Confirm-button colour; empty = the dialog's default primary button. */
  confirmClassName: string;
  icon: LucideIcon;
  /** BLU-717 S3: only garment pre-print creation is gated on the floor's permission. */
  gatedByPreprintPermission?: boolean;
}

/**
 * Everything that varies per bulk disposition, in ONE place: the selection
 * subset it targets, its copy, and its styling. Both the selection toolbar and
 * the shared confirmation dialog read from here, so adding a disposition is a
 * single entry rather than a hunt through five parallel branches.
 * Insertion order is the toolbar's render order.
 */
const BULK_ACTIONS: Record<CancelledItemAction, BulkActionSpec> = {
  return_to_shelf: {
    source: 'blank',
    toolbarKey: 'issues.cancelled.bulk.returnToShelf',
    confirmBodyKey: 'issues.cancelled.bulk.confirmBodyReturn',
    confirmLabelKey: 'issues.cancelled.returnToShelf',
    toolbarClassName: 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90',
    confirmClassName: '',
    icon: Package,
  },
  add_to_preprint: {
    source: 'printed',
    toolbarKey: 'issues.cancelled.bulk.addToPreprint',
    confirmBodyKey: 'issues.cancelled.bulk.confirmBodyAddPreprint',
    confirmLabelKey: 'issues.cancelled.addToPreprint',
    toolbarClassName: 'bg-emerald-600 text-white hover:bg-emerald-700',
    confirmClassName: 'bg-emerald-600 text-white hover:bg-emerald-700',
    icon: Recycle,
    gatedByPreprintPermission: true,
  },
  mark_defect: {
    source: 'printed',
    toolbarKey: 'issues.cancelled.bulk.discard',
    confirmBodyKey: 'issues.cancelled.bulk.confirmBodyDiscard',
    confirmLabelKey: 'issues.cancelled.discard',
    toolbarClassName: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    confirmClassName: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    icon: XCircle,
  },
};

const BULK_ACTION_ENTRIES = Object.entries(BULK_ACTIONS) as Array<
  [CancelledItemAction, BulkActionSpec]
>;

/** Stable empty selection — keeps `pendingBulkIds` referentially steady while no action is pending. */
const NO_BULK_IDS: string[] = [];

/** Debounce time for duplicate scans (ms) */
const SCAN_DEBOUNCE_MS = 500;

/** Time to auto-clear highlight after scan (ms) */
const HIGHLIGHT_CLEAR_MS = 3000;

/** Server page size — mirrors PrintFileIssuesList. */
const PAGE_SIZE = 25;

interface CancelledItemsListProps {
  locationId: string;
}

export function CancelledItemsList({ locationId }: CancelledItemsListProps) {
  const { t } = useTranslation('production');
  // Server-side pagination (RPC-backed, exact total). Replaces the old
  // unpaginated read that 500'd at busy locations (PostgREST URL overflow).
  const [page, setPage] = useState(1);
  const { items, total, isLoading, isError, refetch } = useCancelledItems(locationId, page, PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Processing the last item of the last page shrinks totalPages under us —
  // clamp instead of showing an empty page.
  const safePage = Math.min(page, totalPages);
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);
  const processItem = useProcessCancelledItem();
  const utils = trpc.useUtils();
  // BLU-717 S3: garment pre-print creation gate — read ONCE for the whole
  // list, not per-card. Gate on `blocked` only (never `!enabled`): the hook
  // fails OPEN, so `enabled: null` (unknown / unreadable) must keep the
  // "Add to pre-printed stock" action live.
  const preprintPermission = usePreprintPermission(locationId, 'garment');

  // Bulk selection (PrintFileIssuesList pattern) — page-scoped, cleared on
  // pagination. Every rendered item is selectable (all are actionable).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickedIndexRef = useRef<number | null>(null);
  const [pendingBulkAction, setPendingBulkAction] = useState<CancelledItemAction | null>(null);
  /** Sticky, in-surface partial-failure text — a toast alone vanishes under the fold. */
  const [bulkFailure, setBulkFailure] = useState<string | null>(null);
  const bulkProcess = useProcessCancelledItemsBulk();

  // A scanned garment that lives on ANOTHER page: fetched by id and pinned on
  // top of the list so the scan flow works regardless of pagination.
  const [pinnedItem, setPinnedItem] = useState<CancelledItem | null>(null);

  // Track whether a confirmation dialog is open (from CancelledItemCard)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // USB scanners type characters as keyboard input - redirect to scanner field
  useScannerAutoFocus({ paused: isDialogOpen });

  // Track highlighted item from scanner (triggers auto-open dialog in CancelledItemCard)
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Refs for scrolling to items
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Track last scan time for debounce
  const lastScanTime = useRef<number>(0);
  const lastScannedId = useRef<string | null>(null);

  // Ref for highlight clear timeout (for cleanup)
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Group items by type for badge counts
  const blankCount = useMemo(
    () => items.filter((i) => i.item_type === 'BLANK').length,
    [items]
  );
  const printedCount = useMemo(
    () => items.filter((i) => i.item_type === 'PRINTED').length,
    [items]
  );

  // Handle QR scan
  const handleScan = useCallback(
    (qrCode: string) => {
      // Decode the QR code
      const payload = decodeGarmentQR(qrCode);
      if (!payload) {
        toast.error(t('issues.cancelled.invalidQr'));
        playErrorBeep();
        return;
      }

      const garmentId = payload.g;

      // Debounce: Prevent rapid duplicate scans
      const now = Date.now();
      if (
        garmentId === lastScannedId.current &&
        now - lastScanTime.current < SCAN_DEBOUNCE_MS
      ) {
        toast.info(t('issues.cancelled.alreadyProcessing'));
        playErrorBeep();
        return;
      }
      lastScanTime.current = now;
      lastScannedId.current = garmentId;

      // Find item on the current page (or already pinned from a prior scan).
      // On a miss, look it up server-side by id — the list is paginated, so a
      // valid garment can live on another page (an old order cancelled today
      // sorts by updated_at but can still land off-page). Found → pin it on
      // top, highlighted, so the normal card flow (auto-open confirm for
      // BLANK) proceeds. Not found server-side → genuinely not actionable.
      const item =
        items.find((i) => i.id === garmentId) ??
        (pinnedItem?.id === garmentId ? pinnedItem : undefined);
      if (!item) {
        void (async () => {
          try {
            const page = await utils.productionOrders.issues.getCancelledItems.fetch({
              location_id: locationId,
              garment_unit_id: garmentId,
              limit: 1,
              offset: 0,
            });
            const found = (page as { items: CancelledItem[] }).items[0];
            if (!found) {
              toast.error(t('issues.cancelled.notInList'));
              playErrorBeep();
              return;
            }
            setPinnedItem(found);
            setHighlightedId(found.id);
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = setTimeout(() => {
              setHighlightedId(null);
              highlightTimeoutRef.current = null;
            }, HIGHLIGHT_CLEAR_MS);
          } catch {
            toast.error(t('issues.cancelled.notInList'));
            playErrorBeep();
          }
        })();
        return;
      }

      // Check if already processed (idempotency)
      if (
        ['CANCELLED_RETURNED', 'DEFECT', 'CANCELLED'].includes(item.status)
      ) {
        toast.info(t('issues.cancelled.alreadyProcessed'));
        playErrorBeep();
        return;
      }

      // Clear any existing timeout
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      // Highlight and scroll to item
      setHighlightedId(garmentId);
      const itemRef = itemRefs.current.get(garmentId);
      if (itemRef) {
        itemRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Clear highlight after timeout if no action taken
      // (CancelledItemCard auto-opens dialog when highlighted)
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedId(null);
        highlightTimeoutRef.current = null;
      }, HIGHLIGHT_CLEAR_MS);
    },
    [items, pinnedItem, locationId, utils, t]
  );

  // Handle process action
  const handleProcess = useCallback(
    (garmentId: string, action: 'return_to_shelf' | 'add_to_preprint' | 'mark_defect') => {
      processItem.mutate({
        garment_unit_id: garmentId,
        action,
      });
      if (pinnedItem?.id === garmentId) setPinnedItem(null);
      // Clear highlight after processing
      setHighlightedId(null);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    },
    [processItem, pinnedItem]
  );

  // The pinned (off-page-scanned) card renders on top; drop the pin if paging
  // brings the same garment into view so it never renders twice.
  const displayItems = useMemo(() => {
    if (!pinnedItem) return items;
    if (items.some((i) => i.id === pinnedItem.id)) return items;
    return [pinnedItem, ...items];
  }, [items, pinnedItem]);

  // ---- Bulk selection (mirrors PrintFileIssuesList) -----------------------
  // Every rendered item is actionable, so `displayItems` IS the selectable set.
  const handleToggleRow = useCallback(
    (index: number, id: string, shiftKey: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const last = lastClickedIndexRef.current;
        if (shiftKey && last !== null) {
          const [lo, hi] = last < index ? [last, index] : [index, last];
          for (let i = lo; i <= hi; i++) {
            const row = displayItems[i];
            if (row) next.add(row.id);
          }
        } else if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      lastClickedIndexRef.current = index;
    },
    [displayItems]
  );

  const allSelected = displayItems.length > 0 && displayItems.every((i) => selected.has(i.id));
  const someSelected = displayItems.some((i) => selected.has(i.id));

  const handleSelectAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        displayItems.forEach((i) => next.delete(i.id));
      } else {
        displayItems.forEach((i) => next.add(i.id));
      }
      return next;
    });
    lastClickedIndexRef.current = null;
  }, [allSelected, displayItems]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    lastClickedIndexRef.current = null;
  }, []);

  const selectedBlankIds = useMemo(
    () => displayItems.filter((i) => selected.has(i.id) && i.item_type === 'BLANK').map((i) => i.id),
    [displayItems, selected]
  );
  const selectedPrintedIds = useMemo(
    () => displayItems.filter((i) => selected.has(i.id) && i.item_type === 'PRINTED').map((i) => i.id),
    [displayItems, selected]
  );
  const pendingSpec = pendingBulkAction ? BULK_ACTIONS[pendingBulkAction] : null;
  const pendingBulkIds = useMemo(() => {
    if (!pendingSpec) return NO_BULK_IDS;
    return pendingSpec.source === 'blank' ? selectedBlankIds : selectedPrintedIds;
  }, [pendingSpec, selectedBlankIds, selectedPrintedIds]);
  const pendingBulkCount = pendingBulkIds.length;

  const handleBulkConfirm = useCallback(async () => {
    const action = pendingBulkAction;
    if (!action || pendingBulkIds.length === 0) {
      setPendingBulkAction(null);
      return;
    }
    setBulkFailure(null);
    try {
      const result = await bulkProcess.mutateAsync({
        garment_unit_ids: pendingBulkIds,
        action,
      });
      setPendingBulkAction(null);
      clearSelection();
      if (result.failed.length === 0) {
        toast.success(t('issues.cancelled.bulk.done', { count: result.done }));
      } else {
        const total = result.done + result.failed.length;
        const message = t('issues.cancelled.bulk.partial', { done: result.done, total });
        setBulkFailure(message);
        toast.error(t('issues.cancelled.bulk.partialTitle'), {
          description: message,
          duration: Infinity,
        });
      }
    } catch (error) {
      setPendingBulkAction(null);
      toast.error(getUserMessage(error));
    }
  }, [pendingBulkAction, pendingBulkIds, bulkProcess, clearSelection, t]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full rounded-lg" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertTriangle
          className="h-12 w-12 text-destructive"
          aria-hidden="true"
        />
        <p className="mt-4 text-lg font-medium">{t('issues.cancelled.failedToLoad')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {t('issues.cancelled.pleaseTryAgain')}
        </p>
        <button
          onClick={() => refetch()}
          className="text-sm text-primary hover:underline"
        >
          {t('issues.cancelled.retry')}
        </button>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="space-y-4">
        {/* Scanner still available for checking items */}
        <QRScannerInput
          onScan={handleScan}
          disabled={processItem.isPending}
          isLoading={processItem.isPending}
        />

        <div className="flex flex-col items-center justify-center py-12 text-center">
          <XCircle className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
          <p className="mt-4 text-lg font-medium">{t('issues.cancelled.noItems')}</p>
          <p className="text-sm text-muted-foreground">
            {t('issues.cancelled.willAppearHere')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* QR Scanner Input */}
      <QRScannerInput
        onScan={handleScan}
        disabled={processItem.isPending}
        isLoading={processItem.isPending}
      />

      {/* Selection toolbar — replaces the summary badges when rows are selected */}
      {selected.size > 0 ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-threadheads-purple/30 bg-threadheads-purple/5 px-3 py-2"
          role="region"
          aria-label={t('issues.cancelled.bulk.toolbarLabel')}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-threadheads-purple">
              {t('issues.cancelled.bulk.selectedCount', { count: selected.size })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 text-xs text-muted-foreground"
              onClick={clearSelection}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t('issues.cancelled.bulk.clear')}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {BULK_ACTION_ENTRIES.map(([action, spec]) => {
              const count =
                spec.source === 'blank' ? selectedBlankIds.length : selectedPrintedIds.length;
              // An action with nothing to act on stays hidden entirely.
              if (count === 0) return null;
              const Icon = spec.icon;
              return (
                <Button
                  key={action}
                  size="sm"
                  className={cn('h-8 gap-1.5', spec.toolbarClassName)}
                  disabled={spec.gatedByPreprintPermission && preprintPermission.blocked}
                  onClick={() => setPendingBulkAction(action)}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(spec.toolbarKey, { count })}
                </Button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {blankCount > 0 && (
              <Badge
                variant="outline"
                className={`${cancelledItemConfig.BLANK.color} ${cancelledItemConfig.BLANK.bgColor} border-0`}
              >
                {t('issues.cancelled.blankCount', { count: blankCount })}
              </Badge>
            )}
            {printedCount > 0 && (
              <Badge
                variant="outline"
                className={`${cancelledItemConfig.PRINTED.color} ${cancelledItemConfig.PRINTED.bgColor} border-0`}
              >
                {t('issues.cancelled.printedCount', { count: printedCount })}
              </Badge>
            )}
          </div>
          {displayItems.length > 0 && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                aria-label={t('issues.cancelled.bulk.selectAll')}
                className="pointer-events-none"
              />
              {t('issues.cancelled.bulk.selectAll')}
            </button>
          )}
        </div>
      )}

      {/* BLU-717 S3: pre-print unavailable at this floor — same visible-text
          rule as the card (never hover-only). */}
      {selected.size > 0 && selectedPrintedIds.length > 0 && preprintPermission.blocked && (
        <p role="note" className="text-[11px] leading-snug text-muted-foreground">
          {t('issues.cancelled.preprintDisabledReason', {
            location: preprintPermission.locationName ?? '',
          })}
        </p>
      )}

      {/* Sticky partial-failure text from the last bulk action — a toast alone
          would vanish under the fold. */}
      {bulkFailure && (
        <p role="alert" className="text-sm text-destructive">
          {bulkFailure}
        </p>
      )}

      {/* Items list */}
      <div className="space-y-4">
        {displayItems.map((item, index) => (
          <CancelledItemCard
            key={item.id}
            ref={(el) => {
              if (el) {
                itemRefs.current.set(item.id, el);
              } else {
                itemRefs.current.delete(item.id);
              }
            }}
            item={item}
            onProcess={handleProcess}
            isProcessing={
              processItem.isPending &&
              processItem.variables?.garment_unit_id === item.id
            }
            highlighted={highlightedId === item.id}
            onDialogOpenChange={setIsDialogOpen}
            preprintBlocked={preprintPermission.blocked}
            locationName={preprintPermission.locationName}
            selected={selected.has(item.id)}
            onToggleSelect={(shiftKey) => handleToggleRow(index, item.id, shiftKey)}
          />
        ))}
      </div>

      {/* Pagination (server-side, exact total from the list RPC) */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => {
              setPage(Math.max(1, safePage - 1));
              clearSelection();
            }}
          >
            {t('issues.pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('issues.pagination.pageOf', { current: safePage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => {
              setPage(safePage + 1);
              clearSelection();
            }}
          >
            {t('issues.pagination.next')}
          </Button>
        </div>
      )}

      {/* Bulk-action confirmation — ONE dialog, reused per action (copy, colour
          and target subset all come from BULK_ACTIONS).
          Plain Button (not AlertDialogAction) so the dialog stays open through
          the mutation — AlertDialogAction closes synchronously on click, which
          would hide the spinner on a 200-unit run. Matches
          OrderChangeIssuesList's bulk-dismiss confirm. */}
      <AlertDialog
        open={pendingBulkAction !== null}
        onOpenChange={(open) => {
          if (!open && !bulkProcess.isPending) setPendingBulkAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.cancelled.bulk.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSpec && t(pendingSpec.confirmBodyKey, { count: pendingBulkCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12" disabled={bulkProcess.isPending}>
              {t('issues.cancelled.cancel')}
            </AlertDialogCancel>
            <Button
              className={cn('h-12 gap-1.5', pendingSpec?.confirmClassName)}
              disabled={bulkProcess.isPending}
              onClick={handleBulkConfirm}
            >
              {bulkProcess.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {pendingSpec && t(pendingSpec.confirmLabelKey)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
